import * as React from 'react';
import { Dropdown, DropdownLayoutOption, Input } from 'wix-style-react';
import { translate, InjectedTranslateProps } from 'react-i18next';
import { DataHooks } from '@src/constants/data-hooks';
import { ISitePage, PrivacyPolicyType, AppSettings, TranslationsMap, TranslationKey } from '@src/types';
import { AppContext } from '@src/context/store';
import {
  getSitePages,
  getPrivacyPolicyType,
  getPrivacyPolicyPage,
  getPrivacyPolicyUrl,
} from '@src/context/selectors/app-settings';
import { Actions } from '@src/context/actions/actions';
import { isPrivacyPolicyLinkInvalid } from '@src/context/selectors/validators';

type MappedActions = Pick<Actions, 'updatePendingAppSettings' | 'updatePendingTranslations' | 'validatePrivacyPolicy'>;
interface Props extends MappedActions, InjectedTranslateProps {
  type: PrivacyPolicyType;
  pages: ISitePage[];
  url: string;
  sitePage: string;
  invalid: boolean | null;
}

const Component = translate()((props: Props) => {
  const { t, type, url, sitePage, pages, invalid, validatePrivacyPolicy } = props;

  const onChangePage = async (pageId: string) => {
    await props.updatePendingAppSettings({ privacyPolicyPage: getPagePath(pageId) });
    validatePrivacyPolicy();
  };
  const onChangeUrl = async (updatedUrl: string) => {
    await props.updatePendingTranslations({ [TranslationKey.PolicyUrl]: updatedUrl });
    validatePrivacyPolicy();
  };

  const getPageId = (path: string) => pages.find((page) => page.path === path)?.pageId;
  const getPagePath = (pageId: string) => pages.find((page) => page.pageId === pageId)?.path;

  if (type === PrivacyPolicyType.PageOnSite) {
    if (pages.length) {
      const pageOptions = pages.map((page) => ({ id: page.pageId, value: page.title } as DropdownLayoutOption));
      return (
        <Dropdown
          dataHook={DataHooks.PrivacyPolicyPage}
          placeholder={t('privacy-policy.location.page-on-site.choose-page')}
          initialSelectedId={getPageId(sitePage)}
          onSelect={(option) => onChangePage(option.id as string)}
          options={pageOptions}
          status={invalid ? 'error' : undefined}
          statusMessage={invalid ? t('privacy-policy.location.page-on-site.missing-page') : undefined}
        />
      );
    }

    return null;
  }

  return (
    <Input
      dataHook={DataHooks.PrivacyPolicyUrl}
      placeholder={t('privacy-policy.location.external-url.placeholder')}
      onChange={(event) => onChangeUrl(event.target.value)}
      value={url}
      status={invalid ? 'error' : undefined}
      statusMessage={invalid ? t('privacy-policy.location.external-url.invalid-url') : undefined}
    />
  );
});

export const PolicyTypeInput = translate()(() => {
  const state = React.useContext(AppContext);

  return (
    <Component
      pages={getSitePages(state)}
      type={getPrivacyPolicyType(state)}
      sitePage={getPrivacyPolicyPage(state)}
      url={getPrivacyPolicyUrl(state)}
      invalid={isPrivacyPolicyLinkInvalid(state)}
      updatePendingAppSettings={state.updatePendingAppSettings}
      updatePendingTranslations={state.updatePendingTranslations}
      validatePrivacyPolicy={state.validatePrivacyPolicy}
    />
  );
});
